// src/firebase/dataProvider.js
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "./firebase";


function assertWriteAllowed(resource) {
  const role = (localStorage.getItem("role") || "").toLowerCase();
  if (["docs_link", "operation"].includes(role)) {
    throw new Error(`Read-only role cannot modify ${resource}.`);
  }
}

// helper: read whole collection
async function listAll(collName) {
  const snap = await getDocs(collection(db, collName));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// helper: normalize yyyy-mm-dd strings into Date
function toDateObj(val) {
  if (!val) return null;
  // Firestore may store date as string "2025-10-28"
  // or JS Date, or Timestamp. We'll handle all.
  if (val instanceof Date) return val;
  if (val?.toDate) return val.toDate(); // Firestore Timestamp
  if (typeof val === "string") {
    // try safe parse
    const parts = val.split("-");
    if (parts.length === 3) {
      const [yyyy, mm, dd] = parts;
      return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    }
    return new Date(val);
  }
  return null;
}

export const dataProvider = {
  // LIST
  getList: async (resource, params = {}) => {
    try {
      const { filter = {} } = params;

      // 1. get raw docs
      let data = await listAll(resource);

      // 2. special filtering logic for upcomingExpenses list
      if (resource === "upcomingExpenses") {
        const {
          paymentType,
          startDate,
          endDate,
        } = filter;

        if (paymentType && !/all/i.test(paymentType)) {
  data = data.filter(
    (row) =>
      (row.paymentType || "").toLowerCase() ===
      paymentType.toLowerCase()
  );
}


        // filter by date range if both provided
        // we will treat row.date as planned date
        const startObj = toDateObj(startDate);
        const endObj = toDateObj(endDate);

        if (startObj && endObj) {
          data = data.filter((row) => {
            const rowDateObj = toDateObj(row.date);
            if (!rowDateObj) return false;
            return rowDateObj >= startObj && rowDateObj <= endObj;
          });
        } else if (startObj && !endObj) {
          // only start date given
          data = data.filter((row) => {
            const rowDateObj = toDateObj(row.date);
            if (!rowDateObj) return false;
            return rowDateObj >= startObj;
          });
        } else if (!startObj && endObj) {
          // only end date given
          data = data.filter((row) => {
            const rowDateObj = toDateObj(row.date);
            if (!rowDateObj) return false;
            return rowDateObj <= endObj;
          });
        }
      }

      // 3. return like before so RA is happy
      return { data, total: data.length };
    } catch (err) {
      console.error("getList error:", resource, err);
      return { data: [], total: 0 };
    }
  },

  // ONE
  getOne: async (resource, params) => {
    try {
      const ref = doc(db, resource, params.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Document not found");
      return { data: { id: snap.id, ...snap.data() } };
    } catch (err) {
      console.error("getOne error:", resource, err);
      throw err;
    }
  },

  // CREATE
  create: async (resource, params) => {
    try {
      assertWriteAllowed(resource);
      const ref = await addDoc(collection(db, resource), params.data);
      const snap = await getDoc(ref);
      return { data: { id: snap.id, ...snap.data() } };
    } catch (err) {
      console.error("create error:", resource, err);
      throw err;
    }
  },

  // UPDATE
  update: async (resource, params) => {
    try {
      assertWriteAllowed(resource);
      const ref = doc(db, resource, params.id);
      await updateDoc(ref, params.data);
      const snap = await getDoc(ref);
      return { data: { id: snap.id, ...snap.data() } };
    } catch (err) {
      console.error("update error:", resource, err);
      throw err;
    }
  },

  // DELETE
  delete: async (resource, params) => {
    try {
      assertWriteAllowed(resource);
      const ref = doc(db, resource, params.id);
      await deleteDoc(ref);
      return { data: { id: params.id } };
    } catch (err) {
      console.error("delete error:", resource, err);
      throw err;
    }
  },

  // REF ARR
  getMany: async (resource, params) => {
    try {
      const docs = await Promise.all(
        params.ids.map(async (id) => {
          const snap = await getDoc(doc(db, resource, id));
          return snap.exists() ? { id: snap.id, ...snap.data() } : null;
        })
      );
      return { data: docs.filter(Boolean) };
    } catch (err) {
      console.error("getMany error:", resource, err);
      return { data: [] };
    }
  },

  // NOT USED YET
  getManyReference: async () => ({ data: [], total: 0 }),
};
